/**
 * ADL (Activities of Daily Living) statistical trend analysis.
 *
 * Shares the same primitives as the form-submission trend engine
 * (`trendAnalysis.ts` → `calculateStatistics` / z-score / IQR) so the live
 * per-resident panel, the all-residents dashboard endpoint, and the nightly
 * `trendAlertSweep` all reach the same conclusions from one implementation.
 *
 * No Prisma / AWS / Node / Next imports — pure TypeScript only. The input
 * shapes are intentionally structural so both the backend `adl_records` /
 * `bowel_movements` Prisma rows AND the frontend `AdlRecord` / `BowelMovement`
 * types satisfy them without conversion.
 *
 * ── Why ADL needs more than the form engine ─────────────────────────────────
 * The form engine only understands "lower than baseline = concerning". That is
 * correct for intake (less food/fluid is bad), but ADL assistance levels run
 * `independent → total_dependence`, so LOSING independence makes the encoded
 * value go UP. Reusing the form check verbatim would alert on residents who are
 * improving and stay silent on those declining — exactly backwards. Every ADL
 * series therefore carries an explicit concern *direction*:
 *   - "lower"     intake: a drop below baseline is concerning (form-engine semantics)
 *   - "higher"    assistance / continence: a rise above baseline is concerning
 *   - "deviation" Bristol: departure from the resident's own baseline in either
 *                 direction matters (1–2 constipation AND 6–7 diarrhea), so only
 *                 the outlier/variance checks apply — no single "bad" direction.
 */

import {
  calculateStatistics,
  calculateZScore,
  isOutlierIQR,
  type TrendAlert,
} from "./trendAnalysis";

// ── Input shapes (structural — satisfied by both FE and BE row types) ───────────

export interface AdlTrendRecord {
  record_date: string | Date;
  shift_type: string;
  bathing?: string | null;
  dressing?: string | null;
  eating?: string | null;
  toileting?: string | null;
  transferring?: string | null;
  /** US-189: sixth assistance-scored ADL column, same scale as the other five. */
  oral_care?: string | null;
  continence?: string | null;
  meal_intake_percent?: number | null;
  fluid_intake_ml?: number | null;
}

export interface BristolTrendRecord {
  occurred_at: string | Date;
  bristol_type?: number | null;
}

type ConcernDirection = "lower" | "higher" | "deviation";

// ── Ordinal encoders ────────────────────────────────────────────────────────────

/**
 * Encode an ADL assistance level onto a 0–4 dependence scale where HIGHER means
 * MORE dependence (worse). `refused` / `not_performed` are non-measurements, not
 * severity levels (see ADL_ASSISTANCE_LEVEL_VALUES) — they return null so a
 * single refused/skipped task never reads as catastrophic decline.
 */
export function encodeAssistanceLevel(
  level: string | null | undefined,
): number | null {
  switch (level) {
    case "independent":
      return 0;
    case "supervision":
      return 1;
    case "limited_assistance":
      return 2;
    case "extensive_assistance":
      return 3;
    case "total_dependence":
      return 4;
    default:
      // refused, not_performed, null, unknown → not a measurement
      return null;
  }
}

/**
 * Encode continence onto a 0–3 scale where HIGHER means more incontinence
 * (worse). `not_assessed` / null are non-measurements → null.
 */
export function encodeContinenceLevel(
  level: string | null | undefined,
): number | null {
  switch (level) {
    case "continent":
      return 0;
    case "occasionally_incontinent":
      return 1;
    case "frequently_incontinent":
      return 2;
    case "always_incontinent":
      return 3;
    default:
      return null;
  }
}

// ── Series specs ────────────────────────────────────────────────────────────────

interface AdlSeriesSpec {
  /** Stable key for alert ids. */
  key: string;
  /** Human label shown to staff. */
  fieldName: string;
  /** Grouping label (mirrors the form engine's formName). */
  formName: string;
  direction: ConcernDirection;
  /** Pull the (possibly null) numeric value for this series from a record. */
  encode: (r: AdlTrendRecord) => number | null;
  /**
   * Ordinal series (assistance/continence) have no clinically meaningful raw
   * unit, so we suppress currentValue/baselineValue/changePercentage on their
   * alerts to avoid misleading "3.0" values and (critically) a green +% badge
   * for what is actually a decline.
   */
  ordinal: boolean;
  /** Display unit for numeric series (e.g. "%", " ml"). */
  unit?: string;
}

const ADL_GROUP = "Activities of Daily Living";

const ASSISTANCE_SERIES: AdlSeriesSpec[] = (
  [
    ["bathing", "Bathing"],
    ["dressing", "Dressing"],
    ["eating", "Eating"],
    ["toileting", "Toileting"],
    ["transferring", "Transferring"],
    ["oral_care", "Oral Care"],
  ] as const
).map(([key, label]) => ({
  key,
  fieldName: `${label} (assistance)`,
  formName: ADL_GROUP,
  direction: "higher" as const,
  encode: (r: AdlTrendRecord) =>
    encodeAssistanceLevel(r[key] as string | null | undefined),
  ordinal: true,
}));

const ADL_SERIES: AdlSeriesSpec[] = [
  ...ASSISTANCE_SERIES,
  {
    key: "continence",
    fieldName: "Continence",
    formName: ADL_GROUP,
    direction: "higher",
    encode: (r) => encodeContinenceLevel(r.continence),
    ordinal: true,
  },
  {
    key: "meal_intake_percent",
    fieldName: "Meal intake (%)",
    formName: ADL_GROUP,
    direction: "lower",
    encode: (r) =>
      typeof r.meal_intake_percent === "number" ? r.meal_intake_percent : null,
    ordinal: false,
    unit: "%",
  },
  {
    key: "fluid_intake_ml",
    fieldName: "Fluid intake (ml)",
    formName: ADL_GROUP,
    direction: "lower",
    encode: (r) =>
      typeof r.fluid_intake_ml === "number" ? r.fluid_intake_ml : null,
    ordinal: false,
    unit: " ml",
  },
];

/** day → evening → night → on_call, so same-day shifts sort chronologically. */
const SHIFT_ORDER: Record<string, number> = {
  day: 0,
  evening: 1,
  night: 2,
  on_call: 3,
};

// ── Core direction-aware analyzer ───────────────────────────────────────────────

type BareAlert = Omit<TrendAlert, "id" | "residentId" | "residentName">;

function analyzeSeries(values: number[], spec: AdlSeriesSpec): BareAlert[] {
  const alerts: BareAlert[] = [];
  if (values.length < 3) return alerts;

  const stats = calculateStatistics(values);
  const current = values[values.length - 1];
  if (current === undefined) return alerts; // unreachable (length >= 3), satisfies noUncheckedIndexedAccess
  const zScore = calculateZScore(current, stats.mean, stats.stdDev);
  const rising = current > stats.mean;

  // ── Outlier (z-score + IQR), filtered to the concerning direction ───────────
  if (values.length >= 5) {
    const isOutlier =
      Math.abs(zScore) > 2.5 ||
      isOutlierIQR(current, stats.q1, stats.q3, stats.iqr);
    const concerning =
      spec.direction === "deviation"
        ? true
        : spec.direction === "lower"
          ? !rising // a drop below baseline
          : rising; // "higher": a rise above baseline
    if (isOutlier && concerning) {
      const severity: TrendAlert["severity"] =
        Math.abs(zScore) > 3 ? "critical" : "warning";
      alerts.push({
        severity,
        type: rising ? "outlier" : "sudden_drop",
        fieldName: spec.fieldName,
        formName: spec.formName,
        message: outlierMessage(spec, rising),
        details: outlierDetails(spec, current, stats.mean, zScore),
        ...(spec.ordinal
          ? {}
          : {
              currentValue: current,
              baselineValue: stats.mean,
              zScore,
            }),
        detectedAt: new Date(),
        dataPoints: stats.count,
      });
    }
  }

  // ── Directional sustained trend (not meaningful for deviation series) ───────
  if (spec.direction !== "deviation" && values.length >= 5) {
    const change =
      stats.mean !== 0
        ? ((stats.recentMean - stats.mean) / Math.abs(stats.mean)) * 100
        : 0;
    // Gate on the slope SIGN (sustained direction), not the form engine's
    // ±0.5 magnitude bucket — an ordinal 0–4 scale changes too slowly per step
    // to ever clear 0.5, so the % change carries the severity instead.
    const concerningDrop =
      spec.direction === "lower" && stats.slope < 0 && change < -15;
    const concerningRise =
      spec.direction === "higher" && stats.slope > 0 && change > 15;
    if (concerningDrop || concerningRise) {
      const severity: TrendAlert["severity"] =
        Math.abs(change) > 30 ? "critical" : "warning";
      alerts.push({
        severity,
        type: "declining_trend",
        fieldName: spec.fieldName,
        formName: spec.formName,
        message: trendMessage(spec),
        details: trendDetails(spec, stats.recentMean, stats.mean, change),
        ...(spec.ordinal
          ? {}
          : {
              currentValue: stats.recentMean,
              baselineValue: stats.mean,
              // Only "lower" intake carries a % delta — a negative value renders
              // a correct red badge. "higher" rises would render a misleading
              // green +% badge, so they intentionally omit it.
              changePercentage: change,
            }),
        detectedAt: new Date(),
        dataPoints: stats.count,
      });
    }
  }

  // ── Increased variability (applies to every direction, informational) ───────
  if (values.length >= 10) {
    const recentStats = calculateStatistics(values.slice(-7));
    const varianceRatio =
      stats.stdDev > 0 ? recentStats.stdDev / stats.stdDev : 0;
    if (varianceRatio > 1.5) {
      alerts.push({
        severity: "info",
        type: "increased_variance",
        fieldName: spec.fieldName,
        formName: spec.formName,
        message: `${spec.fieldName} readings are more inconsistent than usual`,
        details: `Recent measurements show ${(varianceRatio * 100 - 100).toFixed(0)}% more variation than usual`,
        detectedAt: new Date(),
        dataPoints: stats.count,
      });
    }
  }

  return alerts;
}

// ── Message builders ────────────────────────────────────────────────────────────

function outlierMessage(spec: AdlSeriesSpec, rising: boolean): string {
  if (spec.direction === "deviation")
    return `Unusual change in ${spec.fieldName}`;
  if (spec.direction === "higher")
    return `Sudden increase in assistance needed for ${spec.fieldName}`;
  return `Unusual ${rising ? "increase" : "drop"} in ${spec.fieldName}`;
}

function outlierDetails(
  spec: AdlSeriesSpec,
  current: number,
  mean: number,
  zScore: number,
): string {
  if (spec.ordinal) {
    return `Latest shift charted markedly ${
      current > mean ? "higher dependence" : "more independence"
    } than this resident's baseline (${Math.abs(zScore).toFixed(1)}σ)`;
  }
  const unit = spec.unit ?? "";
  return `Current ${current.toFixed(1)}${unit} is ${Math.abs(zScore).toFixed(1)}σ from baseline ${mean.toFixed(1)}${unit}`;
}

function trendMessage(spec: AdlSeriesSpec): string {
  if (spec.direction === "higher")
    return `Declining independence in ${spec.fieldName}`;
  return `${spec.fieldName} showing sustained decline`;
}

function trendDetails(
  spec: AdlSeriesSpec,
  recentMean: number,
  mean: number,
  change: number,
): string {
  if (spec.ordinal) {
    return `Recent shifts show a sustained increase in assistance required (${Math.abs(change).toFixed(0)}% above baseline)`;
  }
  const unit = spec.unit ?? "";
  return `Recent average ${recentMean.toFixed(1)}${unit} is ${Math.abs(change).toFixed(1)}% below baseline ${mean.toFixed(1)}${unit}`;
}

// ── Public API ──────────────────────────────────────────────────────────────────

/**
 * Analyze a resident's ADL records (and optional bowel-movement events) for
 * concerning statistical trends. Returns alerts in the same `TrendAlert` shape
 * the form engine produces, so all three surfaces render them identically.
 *
 * Note: `bowel_status` (`bm_observed` / `no_bm`) is intentionally NOT trended
 * here — the constipation / BM-gap signal it carries is owned by US-088's
 * care-coordination plugin. Bristol stool type (from `bowel_movements`) carries
 * the actual bowel *trend*.
 */
export function analyzeAdlTrends(
  residentId: string,
  residentName: string,
  records: AdlTrendRecord[],
  bowelMovements: BristolTrendRecord[] = [],
): TrendAlert[] {
  const alerts: TrendAlert[] = [];

  if (records.length >= 3) {
    const sorted = [...records].sort((a, b) => {
      const dateDiff =
        new Date(a.record_date).getTime() - new Date(b.record_date).getTime();
      if (dateDiff !== 0) return dateDiff;
      return (
        (SHIFT_ORDER[a.shift_type] ?? 99) - (SHIFT_ORDER[b.shift_type] ?? 99)
      );
    });

    for (const spec of ADL_SERIES) {
      const values = sorted
        .map(spec.encode)
        .filter((v): v is number => v !== null);
      for (const bare of analyzeSeries(values, spec)) {
        alerts.push({
          ...bare,
          id: idFor(residentId, spec.key, bare.type),
          residentId,
          residentName,
        });
      }
    }
  }

  // ── Bristol stool type (deviation-only) from bowel_movements ────────────────
  if (bowelMovements.length >= 3) {
    const bristolSpec: AdlSeriesSpec = {
      key: "bristol_type",
      fieldName: "Bristol stool type",
      formName: "Bowel Movements",
      direction: "deviation",
      encode: () => null, // unused for Bristol — values built directly below
      ordinal: false,
    };
    const values = [...bowelMovements]
      .sort(
        (a, b) =>
          new Date(a.occurred_at).getTime() - new Date(b.occurred_at).getTime(),
      )
      .map((b) => b.bristol_type)
      .filter((v): v is number => typeof v === "number" && !isNaN(v));

    for (const bare of analyzeSeries(values, bristolSpec)) {
      alerts.push({
        ...bare,
        id: idFor(residentId, bristolSpec.key, bare.type),
        residentId,
        residentName,
      });
    }
  }

  const order = { critical: 0, warning: 1, info: 2 } as const;
  return alerts.sort((a, b) => order[a.severity] - order[b.severity]);
}

function idFor(residentId: string, key: string, type: string): string {
  return `${residentId}-adl-${key}-${type}-${Date.now()}`;
}
